import { mapSiteAccountDtoToModel, mapSiteAccountModelToDto } from '@hotjar/account';
import { mapOrganizationDtoToModel, mapOrganizationModelToDto } from '@hotjar/organization';
import type { Organization as OrganizationModel, OrganizationDto } from '@hotjar/organization';

import type { Site as SiteModel, FeedbackToolsCount } from './model';
import type { FeedbackToolsCountDTO, Site } from './dto';

/**
 * We pass in an already mapped array of organizations so that mapping doesn't need to happen in here.
 */
export function mapSiteDtoToModel(site: Site, organizations?: Array<OrganizationModel>): SiteModel {
  const siteOrganization = site.organization
    ? mapOrganizationDtoToModel(site.organization)
    : organizations?.find((organization) => organization.id === site.organization_id);

  if (!siteOrganization) {
    throw new Error(`No organization found for site: ${site.id}`);
  }

  return {
    account: mapSiteAccountDtoToModel(site.account),
    alwaysOnRateChangeDate: site.always_on_rate_change_date,
    alwaysOnRateInterimState: site.always_on_rate_interim_state,
    alwaysOnRate: site.always_on_rate,
    anonymizeDigits: site.anonymize_digits,
    anonymizeEmails: site.anonymize_emails,
    continuousCaptureEnabled: site.continuous_capture_enabled,
    created: site.created,
    effectivePlanId: site.effective_plan_id,
    effectivePlan: site.effective_plan,
    effectiveAskPlan: site.effective_plan_for_feedback,
    features: site.features,
    firstData: site.first_data,
    id: site.id,
    industry: site.industry,
    interimAlwaysOnRate: site.interim_always_on_rate,
    interimPlanId: site.interim_plan_id,
    interimPlanState: site.interim_plan_state,
    interimSampleRate: site.interim_sample_rate,
    isInPlanChangePeriod: site.is_in_plan_change_period,
    isTwoSkusActionRequired: site.is_two_skus_action_required,
    lastData: site.last_data,
    legalName: site.legal_name,
    lockPlan: site.lock_plan,
    monetaryDiscount: site.monetary_discount,
    name: site.name,
    organizationId: site.organization_id,
    organization: siteOrganization,
    percentageDiscount: site.percentage_discount,
    planChangeDate: site.plan_change_date,
    planId: site.plan_id,
    plan: site.plan,
    privacyPolicyUrl: site.privacy_policy_url,
    recordingCaptureKeystrokes: site.recording_capture_keystrokes,
    sessionCaptureConsoleConsent: site.session_capture_console_consent,
    sampleRateChangeDate: site.sample_rate_change_date,
    sampleRateInterimState: site.sample_rate_interim_state,
    sampleRate: site.sample_rate,
    stateChangeListenMode: site.state_change_listen_mode,
    suppressAll: site.suppress_all,
    suppressLocation: site.suppress_location,
    suppressText: site.suppress_text,
    trial: {
      active: site.trial.active,
      alwaysOnRate: site.trial.always_on_rate,
      daysRemaining: site.trial.days_remaining,
      exists: site.trial.exists,
      plan: site.trial.plan,
      sampleRate: site.trial.sample_rate,
    },
    url: site.url,
    userAttributesEnabled: site.user_attributes_enabled,
    enforcedMfaAt: site.enforced_mfa_at ? new Date(site.enforced_mfa_at * 1000) : null,
  };
}

/**
 * We pass in an already mapped array of organizations so that mapping doesn't need to happen in here.
 */
export function mapSiteModelToDto(site: SiteModel, organizations?: Array<OrganizationDto>): Site {
  const siteOrganization = site.organization
    ? mapOrganizationModelToDto(site.organization)
    : organizations?.find((organization) => organization.id === site.organizationId);

  if (!siteOrganization) {
    throw new Error(`No organization found for site: ${site.id}`);
  }

  return {
    account: mapSiteAccountModelToDto(site.account),
    always_on_rate_change_date: site.alwaysOnRateChangeDate,
    always_on_rate_interim_state: site.alwaysOnRateInterimState,
    always_on_rate: site.alwaysOnRate,
    anonymize_digits: site.anonymizeDigits,
    anonymize_emails: site.anonymizeEmails,
    continuous_capture_enabled: site.continuousCaptureEnabled,
    created: site.created,
    effective_plan_id: site.effectivePlanId,
    effective_plan: site.effectivePlan,
    effective_plan_for_feedback: site.effectiveAskPlan,
    features: site.features,
    first_data: site.firstData,
    id: site.id,
    industry: site.industry,
    interim_always_on_rate: site.interimAlwaysOnRate,
    interim_plan_id: site.interimPlanId,
    interim_plan_state: site.interimPlanState,
    interim_sample_rate: site.interimSampleRate,
    is_in_plan_change_period: site.isInPlanChangePeriod,
    is_two_skus_action_required: site.isTwoSkusActionRequired,
    last_data: site.lastData,
    legal_name: site.legalName,
    lock_plan: site.lockPlan,
    monetary_discount: site.monetaryDiscount,
    name: site.name,
    organization_id: site.organizationId,
    organization: siteOrganization,
    percentage_discount: site.percentageDiscount,
    plan_change_date: site.planChangeDate,
    plan_id: site.planId,
    plan: site.plan,
    privacy_policy_url: site.privacyPolicyUrl,
    recording_capture_keystrokes: site.recordingCaptureKeystrokes,
    sample_rate_change_date: site.sampleRateChangeDate,
    sample_rate_interim_state: site.sampleRateInterimState,
    sample_rate: site.sampleRate,
    state_change_listen_mode: site.stateChangeListenMode,
    suppress_all: site.suppressAll,
    suppress_location: site.suppressLocation,
    suppress_text: site.suppressText,
    trial: {
      active: site.trial.active,
      always_on_rate: site.trial.alwaysOnRate,
      days_remaining: site.trial.daysRemaining,
      exists: site.trial.exists,
      plan: site.trial.plan,
      sample_rate: site.trial.sampleRate,
    },
    url: site.url,
    user_attributes_enabled: site.userAttributesEnabled,
    enforced_mfa_at: site.enforcedMfaAt ? site.enforcedMfaAt.getTime() / 1000 : null,
  };
}

export const mapFeedbackToolsCountDtoToModel = (
  feedbackToolsCount: FeedbackToolsCountDTO,
): FeedbackToolsCount => {
  return {
    incomingFeedbackWidgetsCount: feedbackToolsCount.incoming_feedback_widgets_count,
    surveysCount: feedbackToolsCount.surveys_count,
  };
};
